import { useState } from 'react'
import { FiX, FiMail, FiLock, FiUser } from 'react-icons/fi'
import { useStore } from '@/stores/useStore'
import { login, register } from '@/services/api'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { setAuth } = useStore()
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    confirmPassword: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.email || !formData.password) {
      setError('请填写邮箱和密码')
      return
    }

    if (mode === 'register' && formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致')
      return
    }

    try {
      setLoading(true)

      if (mode === 'login') {
        const { token, user } = await login(formData.email, formData.password)
        setAuth(token, user.email)
        onClose()
      } else {
        const { token, user } = await register(formData.email, formData.password, formData.username)
        setAuth(token, user.email)
        onClose()
      }
    } catch (err: any) {
      console.error('Auth error:', err)
      setError(err.message || (mode === 'login' ? '登录失败' : '注册失败'))
    } finally {
      setLoading(false)
    }
  }

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login')
    setError(null)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-luxury-navy-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 border border-luxury-gold-800/30">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
        >
          <FiX className="w-5 h-5" />
        </button>

        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-white">
            {mode === 'login' ? '登录' : '注册'}
          </h2>
          <p className="text-gray-400 mt-1">
            {mode === 'login' ? '使用邮箱和密码登录' : '创建新账户'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">邮箱</label>
            <div className="relative">
              <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="w-full pl-10 pr-4 py-3 bg-luxury-navy-800 border border-luxury-gold-800/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold-500/50"
              />
            </div>
          </div>

          {/* Username (register only) */}
          {mode === 'register' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">用户名 (可选)</label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="您的用户名"
                  className="w-full pl-10 pr-4 py-3 bg-luxury-navy-800 border border-luxury-gold-800/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold-500/50"
                />
              </div>
            </div>
          )}

          {/* Password */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">密码</label>
            <div className="relative">
              <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-3 bg-luxury-navy-800 border border-luxury-gold-800/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold-500/50"
              />
            </div>
          </div>

          {/* Confirm Password (register only) */}
          {mode === 'register' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">确认密码</label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 bg-luxury-navy-800 border border-luxury-gold-800/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-luxury-gold-500/50"
                />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-900/50 border border-red-800/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-luxury-gold-600 to-luxury-gold-500 text-luxury-navy-950 font-semibold rounded-lg hover:from-luxury-gold-500 hover:to-luxury-gold-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-luxury-navy-950/30 border-t-luxury-navy-950 rounded-full animate-spin" />
                <span>{mode === 'login' ? '登录中...' : '注册中...'}</span>
              </>
            ) : (
              <span>{mode === 'login' ? '登录' : '注册'}</span>
            )}
          </button>

          {/* Switch Mode */}
          <div className="text-center">
            <button
              type="button"
              onClick={switchMode}
              className="text-sm text-gray-400 hover:text-luxury-gold-500 transition-colors"
            >
              {mode === 'login' ? '没有账户？点击注册' : '已有账户？点击登录'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
